import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, BookOpen } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        credentials: 'include',
      });
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleNavigate = (path, logoType) => {
    localStorage.setItem('siteLogo', logoType);
    navigate(path);
  };

  const kolejLogoSrc = settings.kolej_logo || '/logo.png';
  const kursMerkeziLogoSrc = settings.kurs_merkezi_logo || '/kursmerkezi.png';

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-indigo-50 via-purple-50/50 via-pink-50/30 to-amber-50/40 flex items-center justify-center p-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Animated Background Elements - More Dynamic */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-gradient-to-r from-[#C0A062]/15 via-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-to-r from-blue-400/15 via-indigo-400/10 to-[#C0A062]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-r from-[#C0A062]/8 via-purple-400/8 to-pink-400/8 rounded-full blur-3xl"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#C0A062]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-400/10 to-transparent rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: `linear-gradient(rgba(192,160,98,0.1) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(192,160,98,0.1) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}></div>

      {/* Floating Particles - More Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-float"
            style={{
              width: `${4 + Math.random() * 6}px`,
              height: `${4 + Math.random() * 6}px`,
              backgroundColor: `rgba(192, 160, 98, ${0.2 + Math.random() * 0.3})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 3}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl w-full relative z-10">
        {/* Header Section */}
        <div className="text-center mb-20 animate-fadeInUp">
          <p className="text-2xl md:text-3xl text-gray-700 font-semibold mb-6 tracking-wide" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Lütfen devam etmek istediğiniz siteyi seçin
          </p>
          <div className="flex items-center justify-center gap-4">
            <div className="h-1 w-16 bg-gradient-to-r from-transparent to-[#C0A062] rounded-full"></div>
            <div className="w-3 h-3 bg-[#C0A062] rounded-full animate-pulse"></div>
            <div className="h-1 w-24 bg-gradient-to-r from-[#C0A062] via-purple-500 to-[#C0A062] rounded-full"></div>
            <div className="w-3 h-3 bg-[#C0A062] rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
            <div className="h-1 w-16 bg-gradient-to-l from-transparent to-[#C0A062] rounded-full"></div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-10">
          {/* Kolej Card */}
          <button
            onClick={() => handleNavigate('/kolej', 'kolej')}
            className="group relative bg-gradient-to-br from-white/90 via-white/80 to-white/90 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_25px_80px_-15px_rgba(192,160,98,0.3)] hover:shadow-[0_35px_100px_-20px_rgba(192,160,98,0.5)] transition-all duration-700 overflow-hidden border-2 border-white/30 hover:border-[#C0A062]/50 transform hover:scale-[1.03] hover:-translate-y-3"
          >
            {/* Animated Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/0 via-purple-500/0 to-pink-500/0 group-hover:from-[#C0A062]/10 group-hover:via-purple-500/8 group-hover:to-pink-500/8 transition-all duration-700"></div>
            
            {/* Pattern Overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-700" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(192,160,98,0.3) 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
            
            {/* Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
            </div>

            {/* Decorative Corner Elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-[#C0A062]/5 to-transparent rounded-br-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-purple-500/5 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            {/* Icon Badge */}
            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-[-10px] group-hover:translate-y-0">
              <div className="w-12 h-12 bg-gradient-to-br from-[#C0A062] to-[#9a7d4a] rounded-xl flex items-center justify-center shadow-lg rotate-12 group-hover:rotate-0 transition-transform duration-500">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="relative p-8 md:p-10">
              <div className="flex flex-col items-center">
                {/* Logo Container - Enhanced */}
                <div className="mb-6 h-24 flex items-center justify-center relative">
                  {/* Outer Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/30 via-purple-400/20 to-pink-400/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-700 scale-100 group-hover:scale-125"></div>
                  
                  {/* Middle Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/20 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-700"></div>
                  
                  {/* Logo Frame */}
                  <div className="relative z-10 p-4 bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-2xl border-2 border-[#C0A062]/30 group-hover:border-[#C0A062]/60 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-2xl">
                    <img
                      src={kolejLogoSrc}
                      alt="Serpil Şahin Koleji"
                      className="h-20 w-auto object-contain drop-shadow-2xl"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span
                      className="text-6xl font-bold text-[#C0A062] hidden"
                      style={{ display: 'none' }}
                    >
                      S
                    </span>
                  </div>
                </div>

                {/* Title - Enhanced */}
                <h2 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-gray-900 via-[#C0A062] to-gray-900 bg-clip-text text-transparent mb-4 group-hover:from-[#C0A062] group-hover:via-purple-600 group-hover:to-[#C0A062] transition-all duration-500 tracking-tight" style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 900
                }}>
                  Serpil Şahin Koleji
                </h2>

                {/* Description - Enhanced */}
                <p className="text-gray-700 mb-6 text-center text-lg font-semibold leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Eğitim ve öğretim hizmetleri
                </p>

                {/* CTA Button - Enhanced */}
                <div className="relative group/btn">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#C0A062] via-purple-500 to-[#C0A062] rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#C0A062] via-[#9a7d4a] to-[#C0A062] text-white rounded-full font-bold text-base shadow-2xl shadow-[#C0A062]/40 group-hover:shadow-[#C0A062]/60 group-hover:scale-110 transition-all duration-300" style={{
                    fontFamily: "'Poppins', sans-serif",
                    backgroundSize: '200% auto',
                    animation: 'gradient 3s ease infinite'
                  }}>
                    <span className="mr-2">Devam Et</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            </div>
          </button>

          {/* Kurs Merkezi Card */}
          <button
            onClick={() => handleNavigate('/kolej', 'kurs-merkezi')}
            className="group relative bg-gradient-to-br from-white/90 via-white/80 to-white/90 backdrop-blur-2xl rounded-[2.5rem] shadow-[0_25px_80px_-15px_rgba(192,160,98,0.3)] hover:shadow-[0_35px_100px_-20px_rgba(192,160,98,0.5)] transition-all duration-700 overflow-hidden border-2 border-white/30 hover:border-[#C0A062]/50 transform hover:scale-[1.03] hover:-translate-y-3"
          >
            {/* Animated Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/0 via-purple-500/0 to-pink-500/0 group-hover:from-[#C0A062]/10 group-hover:via-purple-500/8 group-hover:to-pink-500/8 transition-all duration-700"></div>
            
            {/* Pattern Overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-700" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(192,160,98,0.3) 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }}></div>
            
            {/* Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
            </div>

            {/* Decorative Corner Elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-[#C0A062]/5 to-transparent rounded-br-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-purple-500/5 to-transparent rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            {/* Icon Badge */}
            <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-[-10px] group-hover:translate-y-0">
              <div className="w-12 h-12 bg-gradient-to-br from-[#C0A062] to-[#9a7d4a] rounded-xl flex items-center justify-center shadow-lg rotate-12 group-hover:rotate-0 transition-transform duration-500">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="relative p-8 md:p-10">
              <div className="flex flex-col items-center">
                {/* Logo Container - Enhanced */}
                <div className="mb-6 h-24 flex items-center justify-center relative">
                  {/* Outer Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/30 via-purple-400/20 to-pink-400/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-700 scale-100 group-hover:scale-125"></div>
                  
                  {/* Middle Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C0A062]/20 to-transparent rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-700"></div>
                  
                  {/* Logo Frame */}
                  <div className="relative z-10 p-4 bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-2xl border-2 border-[#C0A062]/30 group-hover:border-[#C0A062]/60 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-2xl">
                    <img
                      src={kursMerkeziLogoSrc}
                      alt="Kurs Merkezi"
                      className="h-20 w-auto object-contain drop-shadow-2xl"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span
                      className="text-6xl font-bold text-[#C0A062] hidden"
                      style={{ display: 'none' }}
                    >
                      K
                    </span>
                  </div>
                </div>

                {/* Title - Enhanced */}
                <h2 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-gray-900 via-[#C0A062] to-gray-900 bg-clip-text text-transparent mb-4 group-hover:from-[#C0A062] group-hover:via-purple-600 group-hover:to-[#C0A062] transition-all duration-500 tracking-tight" style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 900
                }}>
                  Kurs Merkezi
                </h2>

                {/* Description - Enhanced */}
                <p className="text-gray-700 mb-6 text-center text-lg font-semibold leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Kurs ve eğitim programları
                </p>

                {/* CTA Button - Enhanced */}
                <div className="relative group/btn">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#C0A062] via-purple-500 to-[#C0A062] rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#C0A062] via-[#9a7d4a] to-[#C0A062] text-white rounded-full font-bold text-base shadow-2xl shadow-[#C0A062]/40 group-hover:shadow-[#C0A062]/60 group-hover:scale-110 transition-all duration-300" style={{
                    fontFamily: "'Poppins', sans-serif",
                    backgroundSize: '200% auto',
                    animation: 'gradient 3s ease infinite'
                  }}>
                    <span className="mr-2">Devam Et</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;

